<script setup lang="ts">
import { onMounted, PropType, Ref, ref, watch } from "vue";
// @ts-ignore
import { VueTelInput } from "vue-tel-input";
import AdminUnits from "./AdminUnits.vue";
import { Country, FormCorporate } from "./types";
import api from "@/app/services/api.ts";
import type { AxiosResponse } from "axios";
import PhoneInput from "@/components/PhoneInput/PhoneInput.vue";

const props = defineProps({
  active: Boolean,
  modelValue: {
    type: Object as PropType<FormCorporate>,
  },
  data: {
    type: Object as PropType<FormCorporate>,
  },
})

const countries:Ref<Country[]> = ref([])

const emit = defineEmits<{
  (e: 'update:modelValue', value: FormCorporate): void
}>()

const individual = ref<FormCorporate>(
  {
    registrationNumber: '',
    name: '',
    registrationDate: '',
    countryId:'',
    email: '',
    phone: '',
    address: {
      districtId: '',
      countyId: '',
      subCountyId: '',
      parishId: '',
      villageId: '',
      description: ""
    },
  },
)

onMounted(() => {
  fetchCountries()
})

watch(
  individual,
  (newValue) => {
    emit('update:modelValue', newValue)
  },
  { deep: true },
)

watch(() => props.data, () => {
  individual.value = props.data ?? individual.value
})

const fetchCountries = async () => {
  return api
      .get(`/configuration/address/country`)
      .then((response: AxiosResponse<Country[]>) => {
        const raw = Array.isArray(response.data) ? response.data : (response.data as any).data ?? []
        countries.value = raw.filter((n: any) => n != null)
      })
}

const errors = ref({
  name: '',
  registrationNumber: '',
  registrationDate: '',
  countryId: '',
  email: '',
  phone: '',
})

function validateName() {
  if (!individual.value.name) {
    errors.value.name = 'Company name is required'
  } else {
    errors.value.name = ''
  }
}

function validateRegistrationNumber() {
  if (!individual.value.registrationNumber) {
    errors.value.registrationNumber = 'Registration number is required'
  } else {
    errors.value.registrationNumber = ''
  }
}

function validateRegistrationDate() {
  if (!individual.value.registrationDate) {
    errors.value.registrationDate = 'Registration date is required'
  } else {
    errors.value.registrationDate = ''
  }
}

function validateCountryId() {
  if (!individual.value.countryId) {
    errors.value.countryId = 'Country of incorporation is required'
  } else {
    errors.value.countryId = ''
  }
}

function validateEmail() {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!individual.value.email) {
    errors.value.email = 'Email is required'
  } else if (!emailRegex.test(individual.value.email)) {
    errors.value.email = 'Please enter a valid email address'
  } else {
    errors.value.email = ''
  }
}

function validatePhone() {
  const phoneRegex = /^(\+?256|0)[1-9]\d{8}$/
  if (!individual.value.phone) {
    errors.value.phone = 'Phone number is required'
  } else if (!phoneRegex.test(individual.value.phone.replace(/\s/g, ''))) {
    errors.value.phone = 'Please enter a valid phone number (e.g. 07XXXXXXXX or +256XXXXXXXXX)'
  } else {
    errors.value.phone = ''
  }
}
</script>

<template>
  <div class="flex flex-col space-y-3">
    <div class="border border-gray-200 rounded-lg p-4">
      <div class="grid grid-cols-2 gap-3">

        <!-- Company Name -->
        <div class="col-span-2">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-building text-gray-400 mr-1"></i>
            Entity Name <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <input
            v-model="individual.name"
            type="text"
            required
            placeholder="Enter company name"
            @blur="validateName"
            @input="validateName"
            :class="['w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary', errors.name ? 'border-red-500' : 'border-gray-300']"
          />
          <p v-if="errors.name" class="mt-1 text-xs text-red-600">{{ errors.name }}</p>
        </div>

        <!-- Registration Number -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-hashtag text-gray-400 mr-1"></i>
            Registration Number <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <input
            v-model="individual.registrationNumber"
            type="text"
            required
            placeholder="Enter registration number"
            @blur="validateRegistrationNumber"
            @input="validateRegistrationNumber"
            :class="['w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary', errors.registrationNumber ? 'border-red-500' : 'border-gray-300']"
          />
          <p v-if="errors.registrationNumber" class="mt-1 text-xs text-red-600">{{ errors.registrationNumber }}</p>
        </div>

        <!-- Registration Date -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-calendar text-gray-400 mr-1"></i>
            Registration Date <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <VueDatePicker
              v-model="individual.registrationDate"
              format="dd-MM-yyyy"
              model-type="format"
              auto-apply
              :max-date="new Date()"
              @update:model-value="validateRegistrationDate"
              :input-class-name="['w-full px-3 py-2 border rounded text-sm', errors.registrationDate ? 'border-red-500' : 'border-gray-300']"
          ></VueDatePicker>
          <p v-if="errors.registrationDate" class="mt-1 text-xs text-red-600">{{ errors.registrationDate }}</p>
        </div>

        <!-- Country of Incorporation -->
        <div class="col-span-2">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-earth-africa text-gray-400 mr-1"></i>
            Country of Incorporation <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <select
              v-model="individual.countryId"
              required
              @blur="validateCountryId"
              @change="validateCountryId"
              :class="['w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary', errors.countryId ? 'border-red-500' : 'border-gray-300']"
          >
            <option value="">Select country</option>
            <option
                v-for="(country, idx) in countries"
                :key="idx"
                :value="country.id">
              {{ country.name }}
            </option>
          </select>
          <p v-if="errors.countryId" class="mt-1 text-xs text-red-600">{{ errors.countryId }}</p>
        </div>

        <!-- Email -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-envelope text-gray-400 mr-1"></i>
            Email <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <input
            v-model="individual.email"
            type="email"
            required
            placeholder="Enter email"
            @blur="validateEmail"
            @input="validateEmail"
            :class="['w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary', errors.email ? 'border-red-500' : 'border-gray-300']"
          />
          <p v-if="errors.email" class="mt-1 text-xs text-red-600">{{ errors.email }}</p>
        </div>

        <!-- Phone -->
        <div>
          <PhoneInput
              v-model="individual.phone"
              label="Phone"
              defaultCountry="+256"
              placeholder="Enter phone number"
              :required="true"
              size="sm"
          />
          <p v-if="errors.phone" class="mt-1 text-xs text-red-600">{{ errors.phone }}</p>
        </div>

        <!-- Address -->
        <div class="col-span-2">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fa-solid fa-map-marker-alt text-gray-400 mr-1"></i>
            Address <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
          </label>
          <AdminUnits :active="active" :isUganda="countries.find(c => c.id == individual.countryId)?.name == 'Uganda'"
                      v-model="individual.address" />
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped></style>